(() => {
  "use strict";

  const instances = new WeakMap();
  const desktopQuery = window.matchMedia("(min-width: 750px)");
  const reducedMotionQuery = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );
  const slideAreas = ["one", "two", "three", "four", "five", "six"];

  function getRealSlides(track) {
    return Array.from(
      track.querySelectorAll(
        ":scope > .bento-grid__slide:not([data-bento-clone])",
      ),
    );
  }

  function getCenteredOffset(track, slide) {
    return slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
  }

  function getActiveSlide(track) {
    const viewportCenter = track.scrollLeft + track.clientWidth / 2;
    return Array.from(
      track.querySelectorAll(":scope > .bento-grid__slide"),
    ).reduce((closest, slide) => {
      if (!closest) return slide;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const closestCenter = closest.offsetLeft + closest.offsetWidth / 2;
      return Math.abs(slideCenter - viewportCenter) <
        Math.abs(closestCenter - viewportCenter)
        ? slide
        : closest;
    }, null);
  }

  function jumpToPosition(track, position) {
    const inlineBehavior = track.style.scrollBehavior;
    const inlineSnapType = track.style.scrollSnapType;
    track.style.scrollBehavior = "auto";
    track.style.scrollSnapType = "none";
    track.scrollLeft = position;
    // Force the browser to apply the instant position before re-enabling snapping.
    void track.offsetWidth;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        track.style.scrollBehavior = inlineBehavior;
        track.style.scrollSnapType = inlineSnapType;
      });
    });
  }

  function jumpToSlide(track, slide) {
    jumpToPosition(track, getCenteredOffset(track, slide));
  }

  function buildSlides(track) {
    const items = Array.from(
      track.querySelectorAll(
        ":scope > .bento-grid__item, :scope > .bento-grid__slide:not([data-bento-clone]) > .bento-grid__item",
      ),
    );
    items.forEach((item) => {
      item.style.removeProperty("--bento-slide-area");
      track.append(item);
    });
    track
      .querySelectorAll(":scope > .bento-grid__slide")
      .forEach((slide) => slide.remove());
    track.classList.remove("bento-grid__track--slider");

    if (!desktopQuery.matches || !items.length) return;

    const slideSize = 6;
    for (let start = 0; start < items.length; start += slideSize) {
      const slideItems = items.slice(start, start + slideSize);
      const slide = document.createElement("div");
      slide.className = "bento-grid__slide";
      slide.setAttribute("role", "group");
      slide.setAttribute(
        "aria-label",
        `Image set ${Math.floor(start / slideSize) + 1}`,
      );

      slideItems.forEach((item, index) => {
        item.style.setProperty("--bento-slide-area", slideAreas[index]);
        slide.append(item);
      });
      track.append(slide);
    }

    const realSlides = getRealSlides(track);
    if (realSlides.length > 1) {
      const lastClone = realSlides.at(-1).cloneNode(true);
      const firstClone = realSlides[0].cloneNode(true);
      lastClone.dataset.bentoClone = "last";
      firstClone.dataset.bentoClone = "first";
      [lastClone, firstClone].forEach((clone) => {
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("inert", "");
      });
      track.prepend(lastClone);
      track.append(firstClone);
    }

    track.classList.add("bento-grid__track--slider");
    requestAnimationFrame(() => jumpToSlide(track, getRealSlides(track)[0]));
  }

  function setup(section) {
    if (instances.has(section)) return;
    const track = section.querySelector("[data-bento-track]");
    const previous = section.querySelector("[data-bento-prev]");
    const next = section.querySelector("[data-bento-next]");
    if (!track) return;

    let frame;
    let settleTimer;
    let lastActiveSlide;
    let lastHasMultipleSlides;
    const supportsScrollEnd = "onscrollend" in track;
    const syncButtons = () => {
      if (!previous || !next) return;
      if (!desktopQuery.matches) {
        section.removeAttribute("data-has-multiple-slides");
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const previousDisabled = track.scrollLeft <= 1;
        const nextDisabled =
          maxScroll === 0 || track.scrollLeft >= maxScroll - 1;
        if (previous.disabled !== previousDisabled)
          previous.disabled = previousDisabled;
        if (next.disabled !== nextDisabled) next.disabled = nextDisabled;
        return;
      }

      const realSlides = getRealSlides(track);
      const hasMultipleSlides = realSlides.length > 1;
      if (hasMultipleSlides !== lastHasMultipleSlides) {
        section.toggleAttribute("data-has-multiple-slides", hasMultipleSlides);
        lastHasMultipleSlides = hasMultipleSlides;
      }

      const activeSlide = getActiveSlide(track);
      if (activeSlide !== lastActiveSlide) {
        Array.from(
          track.querySelectorAll(":scope > .bento-grid__slide"),
        ).forEach((slide) => {
          const isActiveRealSlide =
            slide === activeSlide && !slide.hasAttribute("data-bento-clone");
          slide.toggleAttribute("aria-hidden", !isActiveRealSlide);
          if (isActiveRealSlide) {
            slide.removeAttribute("inert");
          } else {
            slide.setAttribute("inert", "");
          }
        });
        lastActiveSlide = activeSlide;
      }

      if (previous.disabled !== !hasMultipleSlides)
        previous.disabled = !hasMultipleSlides;
      if (next.disabled !== !hasMultipleSlides)
        next.disabled = !hasMultipleSlides;
    };
    const resetLoopPosition = () => {
      const activeSlide = getActiveSlide(track);
      if (!activeSlide?.dataset.bentoClone) return;
      const realSlides = getRealSlides(track);
      const target =
        activeSlide.dataset.bentoClone === "first"
          ? realSlides[0]
          : realSlides.at(-1);
      if (target) {
        jumpToPosition(
          track,
          track.scrollLeft + target.offsetLeft - activeSlide.offsetLeft,
        );
      }
    };
    const handleScrollEnd = () => {
      resetLoopPosition();
      syncButtons();
    };
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        buildSlides(track);
        lastActiveSlide = null;
        lastHasMultipleSlides = undefined;
        syncButtons();
      });
    };
    const scroll = (direction) => {
      if (!desktopQuery.matches) {
        track.scrollBy({
          left: track.clientWidth * direction,
          behavior: reducedMotionQuery.matches ? "auto" : "smooth",
        });
        return;
      }
      const activeSlide = getActiveSlide(track);
      const target =
        direction > 0
          ? activeSlide?.nextElementSibling
          : activeSlide?.previousElementSibling;
      if (!target) return;
      track.scrollTo({
        left: getCenteredOffset(track, target),
        behavior: reducedMotionQuery.matches ? "auto" : "smooth",
      });
    };
    const onScroll = () => {
      if (supportsScrollEnd) return;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(handleScrollEnd, 180);
    };

    previous?.addEventListener("click", () => scroll(-1));
    next?.addEventListener("click", () => scroll(1));
    track.addEventListener("scroll", onScroll, { passive: true });
    track.addEventListener("scrollend", handleScrollEnd);
    const observer = new ResizeObserver(update);
    observer.observe(track);
    desktopQuery.addEventListener("change", update);
    update();

    instances.set(section, { observer, update });
  }

  function setupAll(root = document) {
    root.querySelectorAll("[data-bento-grid]").forEach(setup);
  }

  document.addEventListener("DOMContentLoaded", () => setupAll());
  document.addEventListener("shopify:section:load", (event) =>
    setupAll(event.target),
  );
})();
